import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { downloadFabricImage, formatDate } from '@trinity/utils'
import { ImageNotFound } from '../../assets'
import {
  Box,
  Breadcrumbs,
  Button,
  CardMedia,
  Check,
  Chip,
  ErrorOutline,
  FabricCardList,
  ImageCard,
  GetApp,
  Help,
  IconButton,
  Inventory,
  LocalShippingOutlined,
  NotificationsOutlined,
  Popover,
  Spinner,
  Stack,
  Typography,
  Block,
  type Action,
} from '../../components'
import { useGarmentTypeFilters, useGlobalState, useTheme, type Theme } from '../../hooks'
import { FabricFavorite } from './FabricFavorite'
import { FabricNotificationsForm } from './FabricNotifications'

interface FabricExplorerFabricProps {
  fabric: TrinityAPI.FabricFullType
  fabricImages: TrinityAPI.FabricImageType[]
  relatedFabrics: TrinityAPI.FabricExtendedType[]
  notifications?: TrinityAPI.FabricNotificationType[]
}

export function FabricExplorerFabric({
  fabric,
  notifications = [],
  relatedFabrics,
  fabricImages,
}: FabricExplorerFabricProps) {
  const { onMobile, onTablet } = useGlobalState()
  const scrollRef = useRef<HTMLElement>(null)

  return (
    <Stack spacing={4}>
      <Breadcrumbs aria-label='breadcrumb' sx={{ display: { mobile: 'none', laptop: 'initial' } }}>
        <Typography component={Link} to='/fabric-explorer/collections' variant='smallLink'>
          Collections
        </Typography>
        {fabric.collection && (
          <Typography component={Link} to={`/fabric-explorer/collections/${fabric.collection.id}`} variant='smallLink'>
            {fabric.collection.title}
          </Typography>
        )}
        <Typography variant='smallBody1'>{fabric.trinityFabricNumber}</Typography>
      </Breadcrumbs>
      <Stack direction={{ mobile: 'column', laptop: 'row' }} spacing={{ mobile: 6, laptop: 4 }}>
        {!(onMobile || onTablet) && (
          <Stack flex={1}>
            <FabricImages fabric={fabric} fabricImages={fabricImages} />
          </Stack>
        )}
        <Stack flex={2} spacing={6}>
          <Stack direction='row' spacing={2} justifyContent='space-between'>
            <CardMedia
              component='img'
              src={fabric.collection?.image ?? ImageNotFound}
              alt={fabric.collection?.name ?? 'Collection image'}
              sx={{
                width: { mobile: 90, laptop: 120, desktop: 140 },
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <Stack flex={2} direction='column'>
              <Typography gutterBottom noWrap variant='h4' sx={{ pt: { mobile: 1, tablet: 0.5, desktop: 0 } }}>
                {fabric.trinityFabricNumber}
              </Typography>
              <Typography noWrap variant='body1'>
                {fabric.description}
              </Typography>
            </Stack>
            <Stack>
              <FabricFavorite isLarge fabric={fabric} />
            </Stack>
          </Stack>
          <AvailabilityBanner
            fabric={fabric}
            notifications={notifications}
            scrollTo={() => scrollRef?.current?.scrollIntoView({ behavior: 'smooth' })}
            key={fabric.id}
          />
          {(onMobile || onTablet) && (
            <Stack flex={1} alignItems='center'>
              <FabricImages fabric={fabric} fabricImages={fabricImages} />
            </Stack>
          )}
          <Stack direction={{ mobile: 'column', laptop: 'row' }} spacing={{ mobile: 2, laptop: 12 }}>
            <FabricBodyColumnOne fabric={fabric} />
            <FabricBodyColumnTwo fabric={fabric} />
          </Stack>
        </Stack>
      </Stack>
      <Typography variant='h4' sx={{ pt: 8 }} ref={scrollRef}>
        Related Fabrics
      </Typography>
      <FabricCardList fabrics={relatedFabrics} baseUrl='/fabric-explorer/fabrics/:fabricId' />
    </Stack>
  )
}

//* HELPER COMPONENTS
interface AvailabilityBannerProps {
  fabric: TrinityAPI.FabricFullType
  notifications: TrinityAPI.FabricNotificationType[]
  scrollTo: () => void
}
function AvailabilityBanner({ fabric, notifications, scrollTo }: AvailabilityBannerProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { onMobile } = useGlobalState()
  const theme = useTheme()
  const [showNotify, setShowNotify] = useState<boolean>(false)

  return (
    <Stack
      direction='column'
      p={2}
      spacing={2}
      sx={{
        border: `2px solid ${getBorderColor(fabric.availability, theme)}`,
        backgroundColor: getBgColor(fabric.availability),
        borderRadius: '0.5rem',
        '& .MuiTypography-root': { color: 'neutral' },
      }}
    >
      {/* Line 1: Chip + Button on mobile, Chip + Info + Button on desktop */}
      <Stack direction='row' spacing={1} alignItems='center' flex={1} justifyContent='space-between'>
        <AvailabilityChip availability={fabric.availability} />
        {!onMobile && (
          <>
            {fabric.availability?.restockDate && (
              <Typography variant='smallBody1'>
                Restock: {getRestockDate(fabric.availability)}
                <Typography variant='smallBody1' component='span' paddingX={1}>
                  |
                </Typography>
              </Typography>
            )}
            <Typography variant='smallBody1' sx={{ textTransform: 'capitalize' }}>
              Readiness: {fabric.availability?.readiness ?? 'Discontinued'}
            </Typography>
            <IconButton size='small' onClick={e => setAnchorEl(e.currentTarget)}>
              <Help sx={{ fontSize: 16, color: 'grey.300' }} />
            </IconButton>
          </>
        )}
        {fabric.availability && !fabric.availability?.inStock && (
          <Button
            startIcon={!onMobile ? <NotificationsOutlined /> : null}
            onClick={() => setShowNotify(true)}
            disabled={showNotify}
            title={notifications.length ? 'Edit Notifications' : 'Create a new Notification'}
          >
            {notifications.length ? 'Notifications' : 'Get Notified'}
          </Button>
        )}
        {!fabric.availability && (
          <Button onClick={() => scrollTo()}>{onMobile ? 'Related Fabrics' : 'View Related Fabrics'}</Button>
        )}
      </Stack>

      {/* Line 2 on mobile only: Restock + Readiness + Help */}
      {onMobile && (
        <Stack direction='column' spacing={1} flexWrap='wrap'>
          {fabric.availability?.restockDate && (
            <Typography variant='smallBody1'>Restock: {getRestockDate(fabric.availability)}</Typography>
          )}
          <Stack direction='row'>
            <Typography variant='smallBody1' sx={{ textTransform: 'capitalize' }}>
              Readiness: {fabric.availability?.readiness ?? 'Discontinued'}
            </Typography>
            <IconButton size='small' onClick={e => setAnchorEl(e.currentTarget)}>
              <Help sx={{ fontSize: 16, color: 'grey.300', mt: -1 }} />
            </IconButton>
          </Stack>
        </Stack>
      )}

      {!fabric.availability && (
        <Typography variant='smallBody1'>
          This fabric is no longer produced. View similar options below or contact us for alternatives.
        </Typography>
      )}
      {showNotify && <NotifyMe fabric={fabric} notifications={notifications} setShowNotify={setShowNotify} />}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        marginThreshold={16}
        onClose={() => setAnchorEl(null)}
      >
        <ReadinessPopover />
      </Popover>
    </Stack>
  )
}

function FabricBodyColumnOne({ fabric }: Pick<FabricExplorerFabricProps, 'fabric'>) {
  return (
    <Stack flex={1} spacing={{ mobile: 2, laptop: 6 }}>
      <DefaultSection
        title='collection'
        displayText={fabric.collection?.name ?? 'No Collection'}
        link={fabric.collection ? `/fabric-explorer/collections/${fabric.collection.id}` : null}
      />
      <DefaultSection title='introduced' shouldDisplay={Boolean(fabric.fabricYear)} displayText={fabric.fabricYear} />
      <DefaultSection
        title='garment types'
        shouldDisplay={fabric.fabricGarmentTypes.length > 0}
        displayText={fabric.fabricGarmentTypes.join(', ')}
      />
      <DefaultSection
        title='trim garment types'
        shouldDisplay={fabric.trimGarmentTypes.length > 0}
        displayText={fabric.trimGarmentTypes.join(', ')}
      />
      {fabric.relatedTrim && (
        <Stack spacing={2}>
          <Typography variant='h6' fontSize='label.fontSize'>
            RELATED TRIM
          </Typography>
          <Typography component={Link} to={`/fabric-explorer/fabrics/${fabric.relatedTrim.id}`} variant='link'>
            {fabric.relatedTrim.trinityFabricNumber}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

function FabricBodyColumnTwo({ fabric }: Pick<FabricExplorerFabricProps, 'fabric'>) {
  return (
    <Stack flex={1} spacing={{ mobile: 2, laptop: 6 }}>
      <DefaultSection title='composition' displayText={fabric.composition.description.toLowerCase()} />
      <DefaultSection
        title='weight'
        shouldDisplay={Boolean(fabric.weight)}
        displayText={fabric.weight === 0 ? 'Unknown' : `${fabric.weight} g/m`}
      />
      <DefaultSection
        title='pattern'
        shouldDisplay={Boolean(fabric.fabricPattern?.name)}
        displayText={fabric.fabricPattern?.name}
      />
      <DefaultSection
        title='weave'
        shouldDisplay={Boolean(fabric.fabricWeave?.name)}
        displayText={fabric.fabricWeave?.name}
      />
      <DefaultSection title='price' displayText={fabric.priceTier ? '$'.repeat(Number(fabric.priceTier)) : 'Unknown'} />
      {fabric.discount && (
        <Chip
          color='secondary'
          label={`${Math.ceil(fabric.discount)}% off`}
          sx={{ borderRadius: 1, mt: '0.5rem !important', width: 'fit-content' }}
        />
      )}
    </Stack>
  )
}

interface FabricImagesProps {
  fabric: TrinityAPI.FabricFullType
  fabricImages: TrinityAPI.FabricImageType[]
}
function FabricImages({ fabric, fabricImages }: FabricImagesProps) {
  const { user } = useGlobalState()
  const swatchImage = fabric.hasImage ? fabric.url : ImageNotFound
  const [selectedImage, setSelectedImage] = useState<IMAGETYPE>(SWATCH)

  return (
    <Stack spacing={2} sx={{ width: { mobile: 320, laptop: 600 } }}>
      {selectedImage === SWATCH && (
        <CardMedia
          image={swatchImage}
          sx={{
            height: { mobile: 320, laptop: 600 },
            width: { mobile: 320, laptop: 600 },
            boxShadow: theme => theme.elevation.card2,
            borderRadius: theme => theme.shape.borderRadius,
          }}
        />
      )}
      {selectedImage === GARMENT && <GarmentSelector fabricImages={fabricImages} />}
      <Stack direction={{ mobile: 'column', laptop: 'row' }} alignItems={{ mobile: 'center' }} spacing={2}>
        {selectedImage === SWATCH && (
          <Button
            download
            disabled={!fabric.hasImage}
            size='small'
            variant='outlined'
            color='neutral'
            startIcon={<GetApp />}
            href={downloadFabricImage(fabric.id, user.token, fabric.trinityFabricNumber, swatchImage)}
          >
            Download Image
          </Button>
        )}
        <Stack
          direction='row'
          flex={1}
          alignItems={{ mobile: 'center' }}
          justifyContent={{ laptop: 'flex-end' }}
          spacing={2}
        >
          <Typography
            variant='smallBody1'
            sx={fabricButtonStyles(selectedImage, SWATCH)}
            onClick={() => setSelectedImage(SWATCH)}
          >
            Swatch
          </Typography>
          {!fabric.trimGarmentType && Boolean(fabricImages.length) && (
            <Typography
              variant='smallBody1'
              sx={fabricButtonStyles(selectedImage, GARMENT)}
              onClick={() => setSelectedImage(GARMENT)}
            >
              Garment
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}

interface DefaultSectionProps {
  title: string
  shouldDisplay?: boolean
  displayText?: string | number | null
  link?: string | null
}

function DefaultSection({ title, shouldDisplay = true, displayText = null, link = null }: DefaultSectionProps) {
  if (shouldDisplay) {
    return (
      <Stack spacing={2}>
        <Typography variant='h6' fontSize='label.fontSize' sx={{ textTransform: 'uppercase' }}>
          {title}
        </Typography>
        <Typography
          component={link ? Link : 'p'}
          to={link ?? undefined}
          variant='body1'
          sx={{ textTransform: 'capitalize' }}
        >
          {displayText}
        </Typography>
      </Stack>
    )
  }

  return null
}

const GarmentSelector = ({ fabricImages }: { fabricImages: TrinityAPI.FabricImageType[] }) => {
  const { onSkiffle } = useGlobalState()
  const [fabricImage, setFabricImage] = useState<TrinityAPI.FabricImageType | null>(null)
  const { currentFabrics, model, GarmentFilters } = useGarmentTypeFilters({ fabrics: fabricImages })

  useEffect(() => {
    if (currentFabrics.length > 0) {
      const fabric = currentFabrics.filter(f => f.baseModel.description == model)[0]
      if (fabric) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFabricImage(fabric)
      }
    }
  }, [currentFabrics, model, setFabricImage])

  const actions = !onSkiffle ? [{ type: 'edit', title: 'Edit on Styleflow', intent: 'edit' } satisfies Action] : []

  return (
    <Stack spacing={1} sx={{ height: { mobile: 320, laptop: 600 }, width: { mobile: 320, laptop: 600 } }}>
      <Stack direction='row' justifyContent={{ mobile: 'center', laptop: 'space-between' }}>
        <GarmentFilters />
      </Stack>
      <Stack>
        {fabricImage ? (
          <>
            <ImageCard
              item={{
                key: fabricImage.id,
                image: {
                  uri: fabricImage.image,
                  description: fabricImage.fabric?.description,
                  zoom_uri: fabricImage.picarioUri,
                },
                actions: actions,
                item: fabricImage,
              }}
              width={{ mobile: 320, laptop: 600 }}
              sx={{ height: { mobile: 'auto', laptop: 550 } }}
            >
              <input type='hidden' name='baseModelId' value={fabricImage.baseModel.id} />
              <input type='hidden' name='fabricId' value={fabricImage.fabric?.id} />
              <input type='hidden' name='garmentType' value={fabricImage.garmentType.bitmask} />
            </ImageCard>
          </>
        ) : (
          <Spinner size='8rem' sx={{ alignSelf: 'center' }} />
        )}
      </Stack>
    </Stack>
  )
}

//* HELPER FUNCTIONS
const GARMENT = 'GARMENT'
const SWATCH = 'SWATCH'
type IMAGETYPE = typeof GARMENT | typeof SWATCH

const fabricButtonStyles = (selected: IMAGETYPE, clicked: IMAGETYPE) => ({
  color: selected === clicked ? 'common.white' : 'grey.500',
  border: selected === clicked ? 'primary' : 'n100',
  borderRadius: 1,
  cursor: 'pointer',
  bgcolor: selected === clicked ? 'primary.main' : 'common.white',
  px: 2,
  py: 1,
})

type AvailabilityType = TrinityAPI.AvailabilityType
const atFactory = (readiness: string) => readiness === 'at factory'

const AvailabilityChip = ({ availability = undefined }: { availability?: AvailabilityType }) => {
  const label = !availability ? 'Not Available' : availability.inStock ? 'In Stock' : 'Temp. Out'
  const icon = !availability ? <Block /> : availability.inStock ? <Check /> : <ErrorOutline />
  const color = !availability
    ? 'error'
    : availability.inStock
      ? atFactory(availability.readiness)
        ? 'success'
        : 'info'
      : 'warning'

  return <Chip size='small' label={label} icon={icon} color={color} />
}

const getBorderColor = (avail: AvailabilityType, theme: Theme) =>
  !avail
    ? theme.palette.error.border
    : avail.inStock
      ? atFactory(avail.readiness)
        ? theme.palette.success.border
        : theme.palette.info.border
      : theme.palette.warning.border
const getBgColor = (avail?: AvailabilityType) =>
  !avail
    ? 'error.background'
    : avail.inStock
      ? atFactory(avail.readiness)
        ? 'success.background'
        : 'info.background'
      : 'warning.background'
const getRestockDate = (avail?: AvailabilityType) => avail?.restockDate && formatDate(avail.restockDate)

const ReadinessPopover = () => {
  const { onMobile } = useGlobalState()

  return (
    <Stack
      minWidth={{ mobile: 3, laptop: 42 }}
      maxWidth={{ laptop: '50rem' }}
      spacing={1}
      sx={{ p: 4, overflow: 'auto' }}
    >
      <Typography gutterBottom variant='h6'>
        Fabric Availability & Readiness
      </Typography>
      {!onMobile && (
        <>
          <Typography variant='body1'>
            A fabric is Available, Temporairily Out, or Not Available. In Stock fabrics can be either At Factory or Ship
            From Vendor. Where an In-Stock fabric is located tells you how long it will take to make the garment. Single
            Cut fabrics must be sent to our factory before a garment can be made.
          </Typography>
        </>
      )}
      <Stack direction='column' spacing={2}>
        <Stack
          direction='row'
          bgcolor='success.background'
          borderRadius='0.5rem'
          border='2px solid #BBF7D0'
          spacing={2}
          p={2}
        >
          <Box
            component='span'
            sx={{ backgroundColor: 'success.main', borderRadius: '0.5rem', p: 1.5, alignSelf: 'flex-start' }}
          >
            <Inventory sx={{ color: 'white', display: 'block' }} />
          </Box>
          <Stack flex={2} direction='column' spacing={1}>
            <Typography variant='h6'>At Factory</Typography>
            <Typography variant='body1'>Fabric is in stock at our factory and can be used immediately.</Typography>
            <Stack direction='row' sx={{ '& .MuiTypography-root': { color: 'neutral.main' } }}>
              <Stack direction='column' flex={1}>
                <Typography variant='smallBody1'>Shirts</Typography>
                <Typography variant='smallBody2'>
                  <strong>2-3 weeks</strong>
                </Typography>
              </Stack>
              <Stack direction='column' flex={1}>
                <Typography variant='smallBody1'>Others</Typography>
                <Typography variant='smallBody2'>
                  <strong>4-5 weeks</strong>
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          direction='row'
          bgcolor='info.background'
          borderRadius='0.5rem'
          border='2px solid #BFDBFE'
          spacing={2}
          p={2}
        >
          <Box sx={{ backgroundColor: 'info.main', borderRadius: '0.5rem', p: 1.5, alignSelf: 'flex-start' }}>
            <LocalShippingOutlined sx={{ color: 'white', display: 'block' }} />
          </Box>
          <Stack flex={2} direction='column' spacing={2}>
            <Typography variant='h6'>Ship From Vendor</Typography>
            <Typography variant='body1'>
              Fabric is in stock but must be shipped to our factory before it can be used.
            </Typography>
            <Stack direction='row'>
              <Stack direction='column' flex={1} sx={{ '& .MuiTypography-root': { color: 'neutral.main' } }}>
                <Typography variant='smallBody1'>Shirts</Typography>
                <Typography variant='smallBody2'>
                  <strong>3-5 weeks</strong>
                </Typography>
              </Stack>
              <Stack direction='column' flex={1} sx={{ '& .MuiTypography-root': { color: 'neutral.main' } }}>
                <Typography variant='smallBody1'>Others</Typography>
                <Typography variant='smallBody2'>
                  <strong>5-6 weeks</strong>
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          direction='row'
          bgcolor='warning.background'
          borderRadius='0.5rem'
          border='2px solid #FED7AA'
          spacing={2}
          p={2}
        >
          <Box
            component='span'
            sx={{
              backgroundColor: 'warning.main',
              borderRadius: '0.5rem',
              p: 1.5,
              alignSelf: 'flex-start',
            }}
          >
            <ErrorOutline sx={{ color: 'white', display: 'block' }} />
          </Box>
          <Stack flex={2} direction='column'>
            <Typography variant='h6'>Temporarily Out of Stock</Typography>
            <Typography variant='body1'>
              Fabric has been reordered but is not availble for use. Sign up for notifications to be alerted when it's
              back.
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction='row'
          bgcolor='error.background'
          borderRadius='0.5rem'
          border='2px solid #FECACA'
          spacing={2}
          p={2}
        >
          <Box
            component='span'
            sx={{
              backgroundColor: 'error.main',
              borderRadius: '0.5rem',
              p: 1.5,
              alignSelf: 'flex-start',
            }}
          >
            <ErrorOutline sx={{ color: 'white', display: 'block' }} />
          </Box>
          <Stack flex={2} direction='column'>
            <Typography variant='h6'>Not Available</Typography>
            <Typography variant='body1'>
              Fabric is no longer being offered by the supplier and cannot be used.
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

interface NotifyMeProps {
  fabric: TrinityAPI.FabricFullType
  notifications: TrinityAPI.FabricNotificationType[]
  setShowNotify: React.Dispatch<React.SetStateAction<boolean>>
}

const NotifyMe = ({ fabric, notifications, setShowNotify }: NotifyMeProps) => {
  const notification = notifications[0]

  const fabricNumber = (
    <Typography component='span' color='primary.main' sx={{ fontWeight: 700 }}>
      {fabric.trinityFabricNumber}
    </Typography>
  )

  return (
    <Stack direction='column' spacing={2}>
      {notification ? (
        <Typography variant='body2'>You are already subscribed to notifications for this fabric.</Typography>
      ) : (
        <Typography variant='body2'>
          Register to receive a notification when fabric {fabricNumber} comes back in stock.
        </Typography>
      )}
      <FabricNotificationsForm notification={notification} onCancel={() => setShowNotify(false)} />
    </Stack>
  )
}
